import './styles/timer.css';
import { useGameStore } from '../../store/gameStore';
import { useShallow } from 'zustand/shallow';
import { useEffect, useState } from 'react';

function Timer() {
  // Subscribe to game store
  const {
    mode,
    timeLeft,
    timerStarted,
    timerStartTime,
    timerAddedTime,
    swapCount,
    moveCount,
    foundWords,
    recentFoundWords,
    gameEnded,
    startTimer,
    setTimeLeft,
    setTimerStartTime,
    setGameEnded,
    addTime,
  } = useGameStore(
    useShallow((state) => ({
      mode: state.mode,
      timeLeft: state.gameData[state.mode].timeLeft,
      timerStarted: state.gameData[state.mode].timerStarted,
      timerStartTime: state.gameData[state.mode].timerStartTime,
      timerAddedTime: state.gameData[state.mode].timerAddedTime,
      swapCount: state.gameData[state.mode].swapCount,
      moveCount: state.gameData[state.mode].moveCount,
      foundWords: state.gameData[state.mode].foundWords,
      recentFoundWords: state.gameData[state.mode].recentFoundWords,
      gameEnded: state.gameData[state.mode].gameEnded,
      startTimer: state.startTimer,
      setTimeLeft: state.setTimeLeft,
      setTimerStartTime: state.setTimerStartTime,
      setGameEnded: state.setGameEnded,
      addTime: state.addTime,
    }))
  );

  const getTimeToAdd = () => {
    if (mode === 'blitzSimple') return 3 * 1001;
    if (mode === 'blitzDeluxe') return 5 * 1001;
    return 0;
  };

  const getDuration = () => {
    if (mode === 'blitzSimple') return 3 * 60 * 1001;
    if (mode === 'blitzDeluxe') return 5 * 60 * 1001;
    return 0;
  };

  const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

  // Timer counting logic
  useEffect(() => {
    const start = timerStartTime;
    let animationFrameId: number;

    const updateTimer = () => {
      const now = new Date().getTime();

      let diff = getDuration() - (now - start) + timerAddedTime;

      if (!gameEnded && diff <= 0) setTimeLeft(diff);

      if (swapCount <= 0 || diff <= 0) {
        setGameEnded();
        cancelAnimationFrame(animationFrameId);
        return;
      }
      setTimeLeft(diff);
      animationFrameId = requestAnimationFrame(updateTimer);
    };

    if (timerStarted) {
      animationFrameId = requestAnimationFrame(updateTimer);
    }
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [timerStarted, swapCount, timerAddedTime]);

  // Start Timer
  useEffect(() => {
    if (moveCount === 1) {
      startTimer();
      setTimerStartTime(new Date().getTime());
    }
  }, [moveCount]);

  const [active, setActive] = useState(false);
  const [transitionBack, setTransitionBack] = useState(false);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);

  // Add extra time when words are found
  useEffect(() => {
    if (initialLoad) {
      setInitialLoad(false);
      return;
    }
    let addedTime = recentFoundWords.length * getTimeToAdd();
    if (timeLeft + addedTime > getDuration()) {
      addedTime = getDuration() - timeLeft;
    }

    // Snap into found color
    setTransitionBack(false);
    setActive(true);

    // Apply transition for the fade back
    setTimeout(() => {
      setTransitionBack(true);
      setActive(false);
    }, 50);

    addTime(addedTime);
  }, [recentFoundWords, foundWords]);

  const getTimeDisplay = () => {
    if (minutes < 0) return '0:00';
    if (seconds < 10) {
      return `${minutes}:0${seconds}`;
    } else {
      return `${minutes}:${seconds}`;
    }
  };

  return (
    <div className="timer-container">
      <span className="timer-label">TIME</span>
      <span
        className={`timer-value ${transitionBack ? 'timer-transition' : ''} ${
          active ? 'active' : ''
        }`}
      >
        {getTimeDisplay()}
      </span>
    </div>
  );
}

export default Timer;
